import defaultStorePlugin from 'store/plugins/defaults';
import {
	AtlBrowserStorageLocal,
	AtlBrowserStorageSession,
} from '@atlassian/browser-storage-controls';
import { fg } from '@atlassian/jira-feature-gating';
import cleanUpPlugin from './controllers/storage-cleanup-plugin/index.tsx';
import type { Storage, StorageNew, ExpirableStorageNew, ExpirableStorage } from './types.tsx';

export const plugins = [defaultStorePlugin, cleanUpPlugin];

export const WebStorageType = {
	LocalStorage: 'LocalStorage',
	SessionStorage: 'SessionStorage',
};

export const createStorageProvider = (
	storageType: (typeof WebStorageType)[keyof typeof WebStorageType],
	appPrefix = '',
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	store: any,
	shouldSerialize = true,
): Storage | StorageNew => {
	const namespacedStore = store.namespace(appPrefix);
	const getKeyWithPrefix = (key: string) =>
		appPrefix?.trim().length ? `${appPrefix}.${key}` : key;
	const storageAPI =
		storageType === WebStorageType.LocalStorage ? AtlBrowserStorageLocal : AtlBrowserStorageSession;
	const browserStorage =
		storageType === WebStorageType.LocalStorage
			? globalThis.localStorage
			: globalThis.sessionStorage;
	return {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		set: (key: string, value: any) => {
			try {
				if (fg('jfp-vulcan-browser-storage-providers')) {
					const prefixedKey = getKeyWithPrefix(key);
					const storageValue = shouldSerialize ? JSON.stringify(value) : value;
					storageAPI.setItem(prefixedKey, storageValue);
				} else {
					namespacedStore.set(key, value);
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('Error setting item in storage:', err);
			}
		},
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		setStrictlyNecessaryItem: (key: string, value: any) => {
			try {
				if (fg('jfp-vulcan-browser-storage-providers')) {
					const prefixedKey = getKeyWithPrefix(key);
					const storageValue = shouldSerialize ? JSON.stringify(value) : value;
					storageAPI.setStrictlyNecessaryItem(prefixedKey, storageValue);
				} else {
					// this has to be done when setStrictlyNecessaryItem comes into usage, and it should have a fallback when FG is turned off
					namespacedStore.set(key, value);
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('Error setting item in storage:', err);
			}
		},
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		get: (key: string): any | undefined => {
			try {
				let item;

				if (fg('jfp-vulcan-browser-storage-providers')) {
					const prefixedKey = getKeyWithPrefix(key);
					// When the feature flag is enabled
					if (appPrefix?.trim().length) {
						// Case 1: App prefix exists (all the existing usages of jira-browser-storage-providers had app prefix)
						item = storageAPI.getItem(prefixedKey);

						if (typeof item === 'undefined' || item === null) {
							// Check old store when data not found in new storage
							const itemFromOldStore = namespacedStore.get(key);
							if (typeof itemFromOldStore !== 'undefined' && itemFromOldStore !== null) {
								// Migrate data from old store to new storage
								const stringValue = JSON.stringify(itemFromOldStore);
								storageAPI.setItem(prefixedKey, stringValue);
								return itemFromOldStore;
							}
						}
						if (typeof item === 'string') {
							// If data found in new storage, return it
							return shouldSerialize ? JSON.parse(item) : item;
						}
					}
					// Case 2: No app prefix
					item = storageAPI.getItem(prefixedKey);

					if (typeof item === 'undefined' || item === null) {
						// Check browser's localStorage for the key
						const itemFromNativeBrowserStorage = browserStorage?.getItem(key);
						if (
							typeof itemFromNativeBrowserStorage !== 'undefined' &&
							itemFromNativeBrowserStorage !== null
						) {
							// Migrate data to new storage
							storageAPI.setItem(prefixedKey, itemFromNativeBrowserStorage);
							return itemFromNativeBrowserStorage;
						}
					}
					if (typeof item === 'string') {
						// If data found in new storage, return it
						return shouldSerialize ? JSON.parse(item) : item;
					}
				}
				// When feature flag is disabled, use fallback to using store
				else {
					return namespacedStore.get(key);
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('Error getting item from storage:', err);
			}
		},
		remove: (key: string) => {
			try {
				if (fg('jfp-vulcan-browser-storage-providers')) {
					const prefixedKey = getKeyWithPrefix(key);
					storageAPI.removeItem(prefixedKey); // remove data from new store
					namespacedStore.remove(key); // remove data from old store as well
				} else {
					namespacedStore.remove(key);
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('Error removing item from storage:', err);
			}
		},
		// Clears the complete storage
		clearAll: () => {
			try {
				if (fg('jfp-vulcan-browser-storage-providers')) {
					storageAPI.clear();
					// clean up old keys too
					namespacedStore.clearAll();
				} else {
					namespacedStore.clearAll();
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('Error clearing storage:', err);
			}
		},
		// Clears all the keys specific to the appPrefix
		clearStorageValues: () => {
			if (!appPrefix) {
				throw Error('appPrefix is necessary to access clearStorageValues method.');
			}
			try {
				if (fg('jfp-vulcan-browser-storage-providers')) {
					for (let i = 0; i < storageAPI.length(); i++) {
						const key = storageAPI.key(i);
						if (key && key.startsWith(`${appPrefix}.`)) {
							storageAPI.removeItem(key);
							i--;
						}
					}
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('Error clearing storage:', err);
			}
		},
		length: (): number | undefined => {
			try {
				if (fg('jfp-vulcan-browser-storage-providers')) {
					return storageAPI.length();
				}
				return browserStorage?.length || 0;
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('Error getting storage length:', err);
				return 0;
			}
		},

		// unstable use it only if its absolutely necessary
		getPrefixedKeysLength: (): number => {
			if (!appPrefix) {
				throw Error('appPrefix is necessary to access getNamespaceKeysLength method.');
			}
			try {
				if (fg('jfp-vulcan-browser-storage-providers')) {
					// For browser storage API
					let count = 0;
					const totalLength = storageAPI.length() || 0;
					for (let i = 0; i < totalLength; i++) {
						const key = storageAPI.key(i);
						if (key && key.startsWith(`${appPrefix}.`)) {
							count++;
						}
					}
					return count;
				}
				// For store library using native storage

				let count = 0;
				const totalLength = browserStorage?.length || 0;
				const namespacePrefix = `__storejs_${appPrefix}_`;
				for (let i = 0; i < totalLength; i++) {
					const key = browserStorage?.key(i);
					if (key && key.startsWith(namespacePrefix)) {
						count++;
					}
				}
				return count;
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Error counting namespace keys:', error);
				return 0;
			}
		},
	};
};

export const createExpirableStorageProvider = (
	storageType: (typeof WebStorageType)[keyof typeof WebStorageType],
	appPrefix: string,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	store: any,
): ExpirableStorage | ExpirableStorageNew => {
	if (!appPrefix) {
		throw Error("appPrefix can not be empty. It's used to separate apps");
	}
	const namespacedStore = store.namespace(appPrefix);
	const getKeyWithPrefix = (key: string) => `${appPrefix}.${key}`;
	const storageAPI =
		storageType === WebStorageType.LocalStorage ? AtlBrowserStorageLocal : AtlBrowserStorageSession;
	const browserStorage =
		storageType === WebStorageType.LocalStorage
			? globalThis.localStorage
			: globalThis.sessionStorage;

	return {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		set: (key: string, value: any, expiration?: number): void => {
			try {
				if (fg('jfp-vulcan-browser-storage-providers')) {
					const prefixedKey = getKeyWithPrefix(key);
					const item = {
						value,
						expiration,
					};
					const serializedValue = JSON.stringify(item);
					storageAPI.setItem(prefixedKey, serializedValue);
				} else {
					namespacedStore.set(key, value, expiration);
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(`Error setting item with key=${key}:`, error);
			}
		},

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		setStrictlyNecessaryItem: (key: string, value: any, expiration?: number): void => {
			try {
				const prefixedKey = getKeyWithPrefix(key);
				const item = {
					value,
					expiration,
				};
				const serializedValue = JSON.stringify(item);
				if (fg('jfp-vulcan-browser-storage-providers')) {
					storageAPI.setStrictlyNecessaryItem(prefixedKey, serializedValue);
				} else {
					// this has to be done when setStrictlyNecessaryItem comes into usage, and it should have a fallback when FG is turned off
					namespacedStore.set(key, value, expiration);
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(`Error setting item with key=${key}:`, error);
			}
		},

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		get: (key: string): any | undefined => {
			try {
				let itemStr;
				if (fg('jfp-vulcan-browser-storage-providers')) {
					const prefixedKey = getKeyWithPrefix(key);
					itemStr = storageAPI.getItem(prefixedKey);
					if (!itemStr) {
						// check in the old store
						const itemFromOldStore = namespacedStore.get(key);
						if (typeof itemFromOldStore !== 'undefined' && itemFromOldStore !== null) {
							const item = {
								value: itemFromOldStore,
								expiration: namespacedStore.getExpiration(key),
							};
							storageAPI.setItem(prefixedKey, JSON.stringify(item)); // migrate to new store with new key
							return itemFromOldStore;
						}
					} else {
						// check if the item is expired
						const item = JSON.parse(itemStr);
						if (item.expiration && item.expiration < Date.now()) {
							storageAPI.removeItem(prefixedKey);
							return;
						}
						return item.value;
					}
				} else {
					itemStr = namespacedStore.get(key);
					return itemStr;
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(`Error getting item with key=${key}:`, error);
			}
		},
		getExpiration: (key: string): number | undefined => {
			try {
				const prefixedKey = getKeyWithPrefix(key);
				let itemStr;
				if (fg('jfp-vulcan-browser-storage-providers')) {
					itemStr = storageAPI.getItem(prefixedKey);
					if (!itemStr) {
						// check in the old store
						const itemFromOldStore = namespacedStore.get(key);
						if (typeof itemFromOldStore !== 'undefined' && itemFromOldStore !== null) {
							const expiration = namespacedStore.getExpiration(key);
							const item = {
								value: itemFromOldStore,
								expiration,
							};
							storageAPI.setItem(prefixedKey, JSON.stringify(item)); // migrate to new store with new key
							return expiration;
						}
					} else {
						// check if the item is expired
						const item = JSON.parse(itemStr);
						if (item.expiration && item.expiration < Date.now()) {
							storageAPI.removeItem(prefixedKey);
							return undefined;
						}
						return item.expiration;
					}
				} else {
					return namespacedStore.getExpiration(key);
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(`Error getting expiration for item with key=${key}:`, error);
				return undefined;
			}
		},

		remove: (key: string): void => {
			try {
				if (fg('jfp-vulcan-browser-storage-providers')) {
					const prefixedKey = getKeyWithPrefix(key);
					storageAPI.removeItem(prefixedKey);
					namespacedStore.remove(key);
				} else {
					namespacedStore.remove(key);
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(`Error removing item with key=${key}:`, error);
			}
		},

		removeExpiredKeys: (): void => {
			try {
				if (fg('jfp-vulcan-browser-storage-providers')) {
					for (let i = 0; i < storageAPI.length(); i++) {
						const key = storageAPI.key(i);
						if (key && key.startsWith(`${appPrefix}.`)) {
							const itemStr = storageAPI.getItem(key);
							if (itemStr) {
								const item = JSON.parse(itemStr);
								if (item.expiration && item.expiration < Date.now()) {
									storageAPI.removeItem(key);
									i--;
								}
							}
						}
					}
					namespacedStore.removeExpiredKeys(); // clean up old keys too
				} else {
					namespacedStore.removeExpiredKeys();
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Error removing expired keys:', error);
			}
		},

		// Clears all the keys specific to the appPrefix
		clearStorageValues: () => {
			try {
				if (fg('jfp-vulcan-browser-storage-providers')) {
					for (let i = 0; i < storageAPI.length(); i++) {
						const key = storageAPI.key(i);
						if (key && key.startsWith(`${appPrefix}.`)) {
							storageAPI.removeItem(key);
							i--;
						}
					}
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('Error clearing storage values:', err);
			}
		},

		// Clears the complete storage
		clearAll: () => {
			try {
				if (fg('jfp-vulcan-browser-storage-providers')) {
					storageAPI.clear();
					// clean up old keys too
					namespacedStore.clearAll();
				} else {
					namespacedStore.clearAll();
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('Error clearing storage:', err);
			}
		},
		length: (): number | undefined => {
			try {
				if (fg('jfp-vulcan-browser-storage-providers')) {
					return storageAPI.length();
				}
				return browserStorage?.length || 0;
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('Error getting storage length:', err);
				return 0;
			}
		},
		// unstable use it only if its absolutely necessary
		getPrefixedKeysLength: (): number => {
			try {
				if (fg('jfp-vulcan-browser-storage-providers')) {
					// For browser storage API
					let count = 0;
					const totalLength = storageAPI.length() || 0;
					for (let i = 0; i < totalLength; i++) {
						const key = storageAPI.key(i);
						if (key && key.startsWith(`${appPrefix}.`)) {
							count++;
						}
					}
					return count;
				}
				// For store library using native storage
				let count = 0;
				const totalLength = browserStorage?.length || 0;
				const namespacePrefix = `__storejs_${appPrefix}_`;
				for (let i = 0; i < totalLength; i++) {
					const key = browserStorage?.key(i);
					if (key && key.startsWith(namespacePrefix)) {
						count++;
					}
				}
				return count;
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Error counting namespace keys:', error);
				return 0;
			}
		},
	};
};
